import { useAdaptivePolling } from "@/hooks/use-adaptive-polling";
import { DataService } from "@/services/data";
import { PermissionAudience, UserGroups, UserProfile, UserType } from "@/types/user";
import { NotificationSummaryType } from "@/types/user/notification";
import Bugsnag from "@bugsnag/js";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./auth";

type UserContextType = {
    user: UserType | null;
    notification_summary: NotificationSummaryType | null;
    mark_notification_as_seen: (id: number, callback?: () => void) => void;
    mark_notification_as_unseen: (id: number, callback?: () => void) => void;
    mark_all_notifications_as_seen: (callback?: () => void) => void;
    check_all_new_notifications: (callback?: () => void) => void;
    has_any_group: (groups: UserGroups[]) => boolean;
    has_all_groups: (groups: UserGroups[]) => boolean;
    has_any_profile: (profiles: UserProfile[]) => boolean;
    /** The user's audience (`permission_audience` from the server), null until the user is loaded. */
    audience: PermissionAudience | null;
    /**
     * Whether an element of the interface declared for `audiences` applies to the user. Same rule as
     * `allowed_audiences` server-side: an element that declares nothing is for the standard audience
     * only. Nothing applies until the user is loaded.
     */
    is_visible_to_audience: (audiences?: readonly PermissionAudience[]) => boolean;
};

/** Audiences of an element that declares none: internal and external users only. */
// eslint-disable-next-line react-refresh/only-export-components
export const STANDARD_AUDIENCE: readonly PermissionAudience[] = ["standard"];

const UserContext = React.createContext<UserContextType>(null!);

export function UserProvider({ children }: { children: React.ReactNode }) {
    const { authenticated, viewAsActive } = useAuth();
    const [user, setUser] = useState<UserType | null>(null);
    const [notification_summary, setNotificationSummary] = useState<NotificationSummaryType | null>(null);

    const fetchNotificationSummary = useCallback(async (controller: AbortController) => {
        const data = await DataService.client.signal(controller).get("/users/notifications/summary/").json();
        setNotificationSummary(data as NotificationSummaryType);
    }, []);

    useEffect(() => {
        if (authenticated) {
            DataService.client
                .get("/users/me/")
                .json()
                .then(data => {
                    if (!data) return;
                    const _user = data as UserType;
                    setUser(_user);
                    localStorage.setItem("current_user", JSON.stringify(_user));
                    Bugsnag.setUser(_user.id.toString(), _user.email, `${_user.first_name} ${_user.last_name}`);
                });
        } else {
            setUser(null);
            localStorage.removeItem("current_user");
        }
    }, [authenticated]);

    // Ajout : reload user à chaque changement de viewAsActive
    useEffect(() => {
        if (authenticated) {
            DataService.client
                .get("/users/me/")
                .json()
                .then(data => {
                    if (!data) return;
                    const _user = data as UserType;
                    setUser(_user);
                    localStorage.setItem("current_user", JSON.stringify(_user));
                    Bugsnag.setUser(_user.id.toString(), _user.email, `${_user.first_name} ${_user.last_name}`);
                });
        }
    }, [viewAsActive]);

    // NOTES: Est-ce vraiment utile ? @wangyannick
    // useEffect(() => {
    //     if (user && user.lms_refresh_token) {
    //         // Vérifie si on est en mode view_as
    //         const isViewAs = localStorage.getItem("view_as_tokens") !== null;

    //         if (isViewAs) {
    //             // En mode view_as, on utilise le lms_refresh_token de l'utilisateur qu'on imite
    //             // Les tokens LMS view_as sont gérés dans lms_data.ts
    //         } else {
    //             // En mode normal, on utilise le lms_refresh_token de l'utilisateur principal
    //             if (LMSAuthService.has_access_expires()) {
    //                 LMSAuthService.refresh(user.lms_refresh_token);
    //             }
    //         }
    //     }
    // }, [user, viewAsActive]);

    useAdaptivePolling(fetchNotificationSummary, {
        intervalMs: 15_000,
        maxBackoffMs: 120_000,
        enabled: authenticated,
        refreshKey: viewAsActive
    });

    const mark_notification_as_seen = async (id: number, callback?: () => void) => {
        await DataService.client.post({}, `/users/notifications/${id}/mark_as_seen/`);
        setNotificationSummary(prev => {
            if (prev) {
                return {
                    ...prev,
                    unseen_count: Math.max(0, prev.unseen_count - 1),
                    recent_notifications: prev.recent_notifications.map(n => (n.id == id ? { ...n, seen: true } : n))
                };
            }
            return prev;
        });
        callback?.();
    };

    const mark_notification_as_unseen = async (id: number, callback?: () => void) => {
        await DataService.client.post({}, `/users/notifications/${id}/mark_as_unseen/`).json();
        setNotificationSummary(prev => {
            if (prev) {
                return {
                    ...prev,
                    unseen_count: Math.max(0, prev.unseen_count + 1),
                    recent_notifications: prev.recent_notifications.map(n => (n.id == id ? { ...n, seen: false } : n))
                };
            }
            return prev;
        });
        callback?.();
    };

    const mark_all_notifications_as_seen = async (callback?: () => void) => {
        await DataService.client.post({}, `/users/notifications/mark_all_as_seen/`).json();
        setNotificationSummary(prev => {
            if (prev) {
                return { ...prev, unseen_count: 0, recent_notifications: prev.recent_notifications.map(n => ({ ...n, seen: true })) };
            }
            return prev;
        });
        callback?.();
    };

    const check_all_new_notifications = async (callback?: () => void) => {
        await DataService.client.post({}, `/users/notifications/check_all_new/`).json();
        setNotificationSummary(prev => {
            if (prev) {
                return { ...prev, new_count: 0, recent_notifications: prev.recent_notifications.map(n => ({ ...n, new: false })) };
            }
            return prev;
        });
        callback?.();
    };

    const has_any_group = (groups: UserGroups[]) => {
        if (groups.length == 0) return true;
        if (user) {
            return user.is_super_admin || groups.filter(p => user.groups.indexOf(p) >= 0).length > 0;
        }
        return false;
    };

    const has_all_groups = (groups: UserGroups[]) => {
        if (user) {
            return user.is_super_admin || groups.filter(p => user.groups.indexOf(p) >= 0).length == groups.length;
        }
        return false;
    };

    const has_any_profile = (profiles: UserProfile[]) => {
        if (profiles.length == 0) return true;
        if (user) {
            return profiles.filter(p => user.profile == p).length > 0;
        }
        return false;
    };

    const audience: PermissionAudience | null = user ? (user.permission_audience ?? "standard") : null;

    const is_visible_to_audience = (audiences: readonly PermissionAudience[] = STANDARD_AUDIENCE) => audience !== null && audiences.includes(audience);

    const value = {
        user,
        audience,
        is_visible_to_audience,
        notification_summary,
        has_any_group,
        has_all_groups,
        has_any_profile,
        mark_notification_as_seen,
        mark_notification_as_unseen,
        mark_all_notifications_as_seen,
        check_all_new_notifications
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUser() {
    return useContext(UserContext);
}
